import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BaseDataApiService } from 'src/app/data-transfer/services/base-data-api-service';
import { PackagingSystemNavigationService } from 'src/app/navigation/services/navigation-services/packaging-system-navigation.service';
import { PackagingUnitNavigationService } from '../../services/navigation-services/packaging-unit-navigation.service';
import { MaterialNavigationService } from '../../services/navigation-services/material-navigation.service';
import { ComponentNavigationService } from '../../services/navigation-services/component-navigation-service';
import { ComponentTypeService } from '../../services/component-type-service';
import { PathResolveService } from 'src/app/services/path-resolve.service';
import { SearchResultDto } from 'src/app/data-transfer/entities/search-dto/search-result-dto';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { PackagingComponentTypesEnum } from 'src/app/model/packaging-component-types-enum';
import { Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { DirectoryDto } from 'src/app/data-transfer/entities/directory-dto';
import { DirectoryApiService } from 'src/app/data-transfer/services/directory-api-service';
import { SimpleAlertDialogComponent, SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { TranslocoService } from '@jsverse/transloco';
import { LanguageHandler } from 'src/app/util/language-handler';
import { ComponentText, MaterialText, PackagingSystemText, PackagingUnitText } from 'src/app/model/path-building-blocks';

interface GroupedResults {
  packagingType: string;
  results: SearchResultDto[];
}

@Component({
   selector: 'app-search-results',
   templateUrl: './search-results.component.html',
   styleUrl: './search-results.component.scss'
})
export class SearchResultsComponent implements OnInit {
  groupedResults: GroupedResults[] = [];
  loading: boolean = true;
  searchControl = new FormControl<string>('');
  dateRange = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });
  directoryList: { id :number, name:string } []=[]
  languageHandler: LanguageHandler;
  startDate:Date | null | undefined;
  endDate:Date | null | undefined;

  constructor(
    public dialogRef: MatDialogRef<SearchResultsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { query: string , startDate: Date, endDate:Date},
    private baseDataApiService: BaseDataApiService,
    private router: Router,
    protected packagingSystemNavigationService: PackagingSystemNavigationService,
    protected packagingUnitNavigationService: PackagingUnitNavigationService,
    protected packagingComponentNavigationService: ComponentNavigationService,
    protected compositeMaterialNavigationService: MaterialNavigationService,
    private componentTypeService: ComponentTypeService,
    private directoryApiService: DirectoryApiService,
    private translateService: TranslocoService,
    private dialog: MatDialog,
  ) {
      this.languageHandler = new LanguageHandler(this.translateService);
  }

  ngOnInit(): void {
    this.getDicrectory();
    this.searchControl.setValue(this.data.query);
    this.baseDataApiService.simpleSearch(this.data.query, this.data.startDate, this.data.endDate)
      .pipe(
        catchError(error => {
          console.error('Error loading search results:', error);
          this.loading = false;
          return of([] as SearchResultDto[]);
        })
      )
      .subscribe((results: SearchResultDto[]) => {
        this.groupedResults = this.groupByPackagingType(results);
        this.loading = false;
      });
  }


  private groupByPackagingType(results: SearchResultDto[]): GroupedResults[] {
    const groups: { [key: string]: SearchResultDto[] } = {};

    results.forEach(item => {
      if(item.packagingType?.toString() == "Packaging Component"){
        item.componentTypeName = this.componentTypeService.getNormalComponentTypeNameById(item.componentType!).replace(/\s/g, "")??'';
      }
      item.directoryName = this.directoryList.find(x=>x.id == item.directoryId)?.name;

      const key = item.packagingType || 'Unknown';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
    });

    const staticOrder = ['Packaging System', 'Packaging Unit', 'Packaging Component', 'Composite Material'];

    const groupedArray: GroupedResults[] = [];

    staticOrder.forEach(orderKey => {
      if (groups[orderKey]) {
        groupedArray.push({
          packagingType: orderKey,
          results: groups[orderKey]
        });
        delete groups[orderKey];
      }
    });

    Object.keys(groups).forEach(key => {
      groupedArray.push({
        packagingType: key,
        results: groups[key]
      });
    });

    return groupedArray;
  }

  onNavigate(item: SearchResultDto): void {
    const type = (item.packagingType || '').toLowerCase();
    if (type === 'packaging system') {
      this.navigateToPackagingSystem(item);
    } else if (type === 'packaging unit') {
      this.navigateToPackagingUnit(item);
    } else if (type === 'packaging component') {
      this.navigateToPackagingComponent(item);
    } else if (type === 'composite material') {
      this.navigateToCompositeMaterial(item);
    } else {
      console.error('Unknown packaging type:', item.packagingType);
    }
  }

  navigateToPackagingSystem(item: SearchResultDto):void {
    if (item == null) { return; }
    this.packagingSystemNavigationService.navigateToPackagingSystem(item.rootId!, item.version ?? undefined);
    this.close();
  }

  navigateToPackagingUnit(item: SearchResultDto):void {
    if (item == null) { return; }
    this.packagingUnitNavigationService.navigateToPackagingUnit(item.rootId!, item.version ?? undefined);
    this.close();
  }

  navigateToPackagingComponent(item: SearchResultDto) {
    if (item == null || item.componentType == null || item.rootId == null)
    { return; }

    const componentTypeName = this.componentTypeService.getComponentTypeNameById(item.componentType);
    if(this.componentTypeService.isDecorationComponentTypeId(item.componentType))
    {
      if(item.version) {
        this.packagingComponentNavigationService.navigateToHistoryDecoration(componentTypeName, item.rootId, item.version);
      } else {
        this.packagingComponentNavigationService.navigateToDecoration(componentTypeName, item.rootId);
      }
    }
    else
    {
      if(item.version) {
        this.packagingComponentNavigationService.navigateToHistoryComponent(componentTypeName, item.rootId, item.version);
      } else {
        this.packagingComponentNavigationService.navigateToComponent(componentTypeName, item.rootId);
      }
    }

    this.close();
  }

  navigateToCompositeMaterial(item: SearchResultDto):void {
    if (item == null) { return; }
    this.compositeMaterialNavigationService.navigateToMaterial(item.rootId!, item.version);
    this.close();
  }

  navigateToDirectory(directoryId:SearchResultDto):void{
    this.router.navigate([PathResolveService.getDirectoryManagementPath(true, directoryId.directoryId?.toString(),this.getTypeName(directoryId))]);
    this.close();
  }

  private getTypeName(result: SearchResultDto){
    switch(result.packagingType){
      case "Packaging System":
        return PackagingSystemText;
      case "Packaging Unit":
        return PackagingUnitText;
      case "Packaging Component":
        return ComponentText;
      case "Composite Material":
        return MaterialText;
      default:
        return PackagingSystemText;
    }
  }

  getDicrectory():void{
    this.directoryApiService.getDirectories().subscribe((rootDir:DirectoryDto)=>{
      this.directoryList = this.flattenDirectories(rootDir);
    })
  }

  flattenDirectories(root:DirectoryDto):{id: number, name:string}[]{
    const result :{id: number, name:string}[]=[];

    const traverse = (dir:DirectoryDto)=>{
      result.push({id: dir.id, name:dir.name});
      dir.childDirectories.forEach(child=>traverse(child));
    }

    traverse(root);
    return(result);
  }


  onSearch(): void {

    const query = this.searchControl.value ?? '';
    if (!this.isValidQuery(query)) {
      return;
    }
    const startDate = this.startDate  = this.dateRange.value?.start ?? undefined;
    const endDate =  this.endDate = this.dateRange.value?.end ?? undefined;
    this.data.query = query;


    this.loading = true;

    this.baseDataApiService.simpleSearch(query, startDate, endDate)
      .pipe(
        catchError(error => {
          console.error('Error loading search results:', error);
          this.loading = false;
          return of([] as SearchResultDto[]);
        })
      )
      .subscribe((results: SearchResultDto[]) => {
        this.groupedResults = this.groupByPackagingType(results);
        this.groupedResults = [...this.groupedResults];
        this.loading = false;
      });
  }

   isValidQuery(query:any) {
    if (!query) return false;
    return true;
  }

  close(): void {
    this.dialogRef.close();
  }
}
